import React, { FC } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { ThemeColorsKeys } from "../../styles/tokens/colors";
import Typography from "../typography/Typography";
import { formatForID, useBackgroundColor } from "../../utils";
import { IconMap } from "../icon/cmsIcons";
import Stack from "../stack/Stack";
import { StyledActionBlock } from "../actionBlock/ActionBlock";

export interface CallOutBannerProps {
  children?: React.ReactNode; // For storybook
  heading?: string;
  icon?: keyof typeof IconMap;
  backgroundColor: ThemeColorsKeys;
  style: "primary" | "secondary";
}

interface StyledHeadingProps {
  displayStyle: "primary" | "secondary";
  backgroundColor: ThemeColorsKeys;
}

interface StyledBodyProps {
  displayStyle: "primary" | "secondary";
}

// Styles

const StyledCallOutBanner = styled.div`
  ${({ theme: { colors, borderRadii, media } }) => css`
    border-radius: ${borderRadii.small};
    border: solid 1px ${colors.darkBlue};
    overflow: hidden;
  `};
`;

const StyledHeading = styled(Typography)<StyledHeadingProps>`
  ${({
    theme: { space, colors, maxMedia },
    displayStyle,
    backgroundColor,
  }) => css`
    ${useBackgroundColor(backgroundColor)}
    padding: ${displayStyle === "primary"
      ? space.fluidSmallToLarge
      : space.medium};
    border-bottom: solid 1px ${colors.darkBlue};
    display: flex;
    gap: ${space.xSmall};

    @media (max-width: ${maxMedia.medium}) {
      flex-direction: column;
    }
  `};
`;

const StyledBody = styled(Stack)<StyledBodyProps>`
  ${({ theme: { space, colors, media }, displayStyle }) => css`
    ${useBackgroundColor("white")}
    padding: ${displayStyle === "primary"
      ? space.fluidSmallToLarge
      : space.medium};

    // Overdie certain styles when nested within the callout banner

    > ${StyledActionBlock} {
      @media (min-width: ${media.medium}) {
        h3,
        p {
          text-wrap: unset;
        }
      }
    }
  `};
`;

// JSX

export const CallOutBanner: FC<CallOutBannerProps> = ({
  children,
  heading,
  icon,
  backgroundColor = "zemplerBlue",
  style = "primary",
}) => {
  let Icon = null;

  // Check that all icons are defined
  Object.entries(IconMap).forEach(([key, value]) => {
    if (!value) {
      console.error(`Icon with key ${key} is undefined.`);
    }
  });

  if (icon) {
    const normalizedIcon = icon.replace(/\s+/g, "");
    const dynamicIconKey =
      normalizedIcon.charAt(0).toUpperCase() + normalizedIcon.slice(1);
    Icon = IconMap[dynamicIconKey as keyof typeof IconMap];
  }

  return (
    <StyledCallOutBanner id={heading ? formatForID(heading) : ""}>
      <StyledHeading
        component="h2"
        variant={style === "primary" ? "headingMedium" : "headingXSmall"}
        displayStyle={style}
        backgroundColor={backgroundColor}
      >
        {Icon && <Icon size="large" />}
        {heading}
      </StyledHeading>
      <StyledBody displayStyle={style} gap="fluidSmallToMedium">
        {children}
      </StyledBody>
    </StyledCallOutBanner>
  );
};

export default CallOutBanner;
